import React, { useEffect, useState } from 'react';
import {
  Box,
  Button,
  Typography,
  Container,
  Stack,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress, // Importa CircularProgress para la animación de carga
} from '@mui/material';
import { Settings, Logout, Login } from '@mui/icons-material';
import SVGComponent from './svgviewer-react-output-remaster.js';
import handleGoogleSignIn from '../handler/handleGoogleSignIn.js';
import { useAuth } from '../auth/AuthContext.js';
import { signOut } from 'firebase/auth';
import { auth } from '../firebase/firebase.js';
import { useNavigate } from 'react-router-dom';
import InstallPWAButton from './SuggestInstall.jsx';
import UserAgreement from './UserAgreementDialog.jsx'; // Importa el componente del acuerdo de usuario

const LoginPage = () => {
  const { currentUser, isLoading, dbUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [signInError, setSignInError] = useState(null);
  const [showInstructions, setShowInstructions] = useState(false);
  const [isSigningIn, setIsSigningIn] = useState(false);
  const [showUserAgreement, setShowUserAgreement] = useState(false); // Nuevo estado para el UserAgreement

  // Función para manejar el inicio de sesión
  const signIn = async () => {
    // Primero, mostrar el acuerdo de usuario si no ha sido aceptado previamente (puedes usar localStorage para esto)
    const hasAcceptedAgreement = localStorage.getItem('userAgreementAccepted');
    if (!hasAcceptedAgreement) {
      setShowUserAgreement(true);
      return; // Detiene el flujo hasta que el usuario acepte
    }

    setSignInError(null);
    setIsSigningIn(true);

    const result = await handleGoogleSignIn();

    setIsSigningIn(false);

    console.log('Redirecting user to its profile to complete information', result);

    if (!result) {
      console.log('Maybe you are not signed in, maybe is because you have by default enabled some kind of add locker integrated into your browser...');
      setSignInError(
        `Vaya! No has podido acceder. Algunos navegadores traen la opción de bloquear anuncios por defecto. Prueba a desactivarlo para registrarte, luego puedes volver a activarlo.`
      );
      return;
    }
    navigate('/misbolos/profile');
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      console.log('User signed out.');
      // Opcional: Remover la aceptación del acuerdo al cerrar sesión para que lo vea de nuevo si es necesario
      localStorage.removeItem('userAgreementAccepted');
    } catch (error) {
      console.error('Error signing out:', error);
      setSignInError('Error al cerrar sesión. Por favor, inténtalo de nuevo.');
    }
  };

  useEffect(() => {
    if (dbUser?.notifications) {
      const filtered = dbUser.notifications.filter(
        (n) => n.type === 'expires-on-read' && n.status === 'pending'
      );
      setNotifications(filtered);
    }
  }, [dbUser]);

  const dismissNotification = (id) => {
    setNotifications((prev) => prev.filter((n) => n._id !== id));
  };

  const handleOpenInstructions = () => {
    setShowInstructions(true);
  };

  const handleCloseInstructions = () => {
    setShowInstructions(false);
  };

  // Funciones para el UserAgreement
  const handleAcceptAgreement = () => {
    localStorage.setItem('userAgreementAccepted', 'true');
    setShowUserAgreement(false);
    signIn(); // Continuar con el proceso de inicio de sesión después de aceptar
  };

  const handleCloseAgreement = () => {
    setShowUserAgreement(false);
    // Puedes añadir lógica aquí si el usuario cierra el acuerdo sin aceptarlo
    // Por ejemplo, mostrar un mensaje o deshabilitar el botón de inicio de sesión.
  };

  return (
    <Box sx={styles.screenContainer}>
      <Container maxWidth="sm">
        <Stack spacing={4} alignItems="center" textAlign="center">
          <Typography variant="h3" fontWeight="bold" sx={styles.title}>
            {!currentUser?.displayName && (
              <SVGComponent style={{ marginRight: 8 }} width={60} height={60} />
            )}
            {currentUser?.displayName || 'OficioMania'}
          </Typography>

          <Typography variant="h6" color="black" sx={styles.subtitle}>
            Actualiza la información de tu perfil regularmente
          </Typography>

          {signInError && (
            <Alert severity="error" sx={{ width: '100%', mb: 2 }}>
              {signInError}
              <Button
                onClick={handleOpenInstructions}
                color="inherit"
                size="small"
                sx={{ mt: 1, display: 'block', mx: 'auto' }}
              >
                ¿Cómo desactivo el bloqueador de anuncios?
              </Button>
              <Button
                onClick={() => navigate('/misbolos/elogin')}
                variant="outlined"
                color="primary"
                size="small"
                sx={{ mt: 1, display: 'block', mx: 'auto' }}
              >
                Iniciar sesión manualmente email/password
              </Button>
            </Alert>
          )}

          <Dialog open={showInstructions} onClose={handleCloseInstructions}>
            <DialogTitle>Cómo desactivar un bloqueador de anuncios</DialogTitle>
            <DialogContent dividers>
              <Typography variant="body1" paragraph>
                (Chrome recomendado !!)
                En dispositivos móviles, algunos navegadores incluyen bloqueadores de anuncios integrados, como **Safari** en iOS o **Brave** en Android. Otros navegadores permiten instalar extensiones con funciones similares.
              </Typography>

              <Typography variant="h6" mt={2} mb={1}>
                Pasos Generales en Móvil:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>**Abre el menú del navegador:** Suele estar representado por tres puntos o líneas en una esquina de la pantalla.</li>
                <li>**Busca opciones relacionadas con bloqueo de contenido:** Puede estar en “Configuración”, “Privacidad” o “Bloqueo de anuncios”.</li>
                <li>**Desactiva el bloqueador para este sitio:** Algunas apps permiten pausar el bloqueo para páginas específicas.</li>
                <li>**Recarga la página:** Esto aplicará los cambios y permitirá que el sitio funcione correctamente.</li>
              </Typography>

              <Typography variant="h6" mt={2} mb={1}>
                Instrucciones Específicas para Móviles:
              </Typography>
              <Typography variant="body2" component="ul" sx={{ pl: 2 }}>
                <li>**Safari en iPhone/iPad:**
                  <Typography variant="body2" component="span" sx={{ display: 'block', ml: 2 }}>
                    1. Ve a "Configuración" del dispositivo.<br />
                    2. Selecciona "Safari" &gt; "Bloqueadores de contenido".<br />
                    3. Desactiva temporalmente los bloqueadores activos.<br />
                    4. Regresa al navegador y recarga la página.
                  </Typography>
                </li>
                <li>**Brave en Android/iOS:**
                  <Typography variant="body2" component="span" sx={{ display: 'block', ml: 2 }}>
                    1. Toca el icono del "león" o "escudo" en la barra de direcciones.<br />
                    2. Desactiva el interruptor que dice "Bloquear anuncios y rastreadores".<br />
                    3. La página se actualizará automáticamente.
                  </Typography>
                </li>
                <li>**Opera en Android/iOS:**
                  <Typography variant="body2" component="span" sx={{ display: 'block', ml: 2 }}>
                    1. Toca el menú (ícono de Opera o tres líneas).<br />
                    2. Ve a "Bloqueo de anuncios".<br />
                    3. Desactívalo para el sitio actual o completamente.<br />
                    4. Recarga la página.
                  </Typography>
                </li>
              </Typography>

              <Typography variant="body2" paragraph mt={2}>
                Si aún tienes problemas, intenta acceder desde otro **navegador móvil** o revisa la **configuración de privacidad** del navegador actual.
              </Typography>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleCloseInstructions}>Cerrar</Button>
            </DialogActions>
          </Dialog>

          {notifications.length > 0 && (
            <Stack spacing={2} width="100%" mt={2}>
              {notifications.map((n) => (
                <Box
                  key={n._id}
                  sx={{
                    backgroundColor: '#fff3e0',
                    border: '1px solid #ffcc80',
                    borderRadius: 2,
                    padding: 2,
                    textAlign: 'left',
                    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                  }}
                >
                  <Typography variant="subtitle1" fontWeight="bold">
                    {n.title}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" mb={1}>
                    {n.message}
                  </Typography>
                  <Button
                    size="small"
                    variant="outlined"
                    color="error"
                    onClick={() => dismissNotification(n._id)}
                  >
                    Descartar
                  </Button>
                </Box>
              ))}
            </Stack>
          )}

          {!isLoading && (
            currentUser ? (
              <Stack spacing={2} width={{ xs: '90%', sm: '70%', md: '60%' }}>
                <Button
                  variant="outlined"
                  startIcon={<Settings />}
                  size="large"
                  onClick={() => navigate('/misbolos/profile')}
                  sx={styles.secondaryButton}
                >
                  Gestionar Mi Perfil
                </Button>

                <Button
                  variant="text"
                  startIcon={<Logout />}
                  size="large"
                  onClick={handleSignOut}
                  sx={styles.textButton}
                >
                  Cerrar Sesión
                </Button>
              </Stack>
            ) : (
              <>
                {isSigningIn ? (
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
                    <CircularProgress />
                    <Typography variant="body1">Comprobando datos...</Typography>
                  </Box>
                ) : (
                  <Button
                    variant="contained"
                    startIcon={<Login />}
                    size="large"
                    onClick={signIn}
                    sx={styles.googleSignInButton}
                    disabled={isSigningIn}
                  >
                    Acceder con Google
                  </Button>
                )}
                <InstallPWAButton>a</InstallPWAButton>
              </>
            )
          )}
        </Stack>
      </Container>
      {/* Integración del UserAgreement */}
      <UserAgreement
        open={showUserAgreement}
        onClose={handleCloseAgreement}
        onAccept={handleAcceptAgreement}
      />
    </Box>
  );
};

const styles = {
  screenContainer: {
    minHeight: '100%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: { xs: 2, sm: 3, md: 4 },
    boxSizing: 'border-box',
  },
  title: {
    color: 'black',
    fontSize: { xs: '2.5rem', sm: '3rem', md: '3.5rem' },
    flexGrow: 1,
    display: 'flex',
    alignItems: 'center',
    padding: '1px',
  },
  subtitle: {
    color: '#424242',
    fontSize: { xs: '1rem', sm: '1.1rem', md: '1.25rem' },
    mb: 3,
  },
  primaryButton: {
    borderRadius: 3,
    paddingY: 1.5,
    backgroundColor: '#000',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#222',
    },
    boxShadow: '0 4px 10px rgba(0, 0, 0, 0.2)',
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  secondaryButton: {
    borderRadius: 3,
    paddingY: 1.5,
    border: '2px solid #000',
    color: '#000',
    backgroundColor: '#fff',
    '&:hover': {
      backgroundColor: '#f5f5f5',
      borderColor: '#222',
    },
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  textButton: {
    color: '#d32f2f',
    '&:hover': {
      backgroundColor: 'rgba(211, 47, 47, 0.05)',
    },
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
  googleSignInButton: {
    backgroundColor: '#4285F4',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#357ae8',
    },
    borderRadius: 3,
    paddingX: 4,
    paddingY: 1.5,
    boxShadow: '0 4px 10px rgba(66, 133, 244, 0.3)',
    textTransform: 'none',
    fontWeight: 'bold',
    fontSize: '1.1rem',
  },
};

export default LoginPage;